<template>
    <div class="app-body" id="view">
        <div class="padding">
            <!-- Information section -->
            <div class="row mb-3">
                <div class="col-12">
                    <el-alert
                        type="success"
                        :closable="false">
                        <template v-slot>
                            <h4 class="mb-2">RCPA</h4>
                            <p class="mb-2 alert-text">Add a Recurring Credit Purchase Agreement (RCPA) matching your plan term:</p>
                            <ul class="list-unstyled mb-2 alert-text">
                                <li>• Monthly RCPA → Monthly Subscription</li>
                                <li>• Quarterly RCPA → Quarterly Subscription</li>
                                <li>• Annually RCPA → Annually Subscription</li>
                            </ul>
                            <p class="mb-4 alert-text">Or select a Monthly RCPA regardless of your current plan</p>
                        </template>
                    </el-alert>
                    <el-alert
                        type="warning"
                        :closable="false">
                        <template v-slot>
                            <h4 class="mb-2">Subscription Payments</h4>
                            <p class="alert-text">The 'Verify Payment' option becomes available again at the start of each new billing cycle</p>
                        </template>
                    </el-alert>
                </div>
            </div>

            <!-- Filters section -->
            <div class="row mb-3 d-flex align-items-center justify-content-between">
                <div class="px-3">
                    <div v-if="pagination.total">
                        <span class="_700">Results:</span> {{ pagination.total }}
                    </div>
                </div>

                <div class="w-80 d-flex justify-content-end px-3 pt-3">
                    <div class="mr-2 w-50">
                        <el-input
                            placeholder="Search for a company name..."
                            prefix-icon="el-icon-search"
                            v-model="filters.search_text"
                            @clear="records = []"
                            @keyup.enter.native="handleSearch"
                            clearable>
                        </el-input>
                    </div>
                    <div>
                        <el-button
                            v-if="showClearFiltersButton"
                            @click="clearFilters()">
                            Reset
                        </el-button>

                        <el-button
                            type="success"
                            @click="handleSearch">
                            Search
                        </el-button>

                        <el-button
                            type="primary"
                            @click.prevent="exportToCsv">
                          <i class="fa fa-table mr-1"></i>
                          Export CSV
                        </el-button>
                    </div>
                </div>
            </div>

            <!-- Table section -->
            <div class="card p-3" id="container-table-recurring-credits">
                <div class="p-3">
                    <el-table
                        stripe
                        :data="records"
                        v-loading="loading"
                        style="width: 100%"
                        @sort-change="handleSortChange">
                        <el-table-column
                            sortable
                            prop="subscription.company.id"
                            label="Company ID"
                            width="130">
                            <template v-slot="scope">
                                <div class="d-flex align-items-center">
                                    <span>#{{ getCompany(scope.row).id }}</span>
                                </div>
                            </template>
                        </el-table-column>

                        <el-table-column
                            sortable
                            prop="subscription.company.name"
                            label="Company Name">
                            <template v-slot="scope">
                                <p class="mb-0">{{ getCompany(scope.row).name }}</p>
                            </template>
                        </el-table-column>

                        <el-table-column
                            label="Subscription Status"
                            width="180">
                            <template v-slot="scope">
                                <span>{{ scope.row.subscription?.status || '-' }}</span>
                            </template>
                        </el-table-column>

                        <el-table-column
                            sortable
                            prop="subscription.company.plan.name"
                            label="Plan Type">
                            <template v-slot="scope">
                                <span>{{ scope.row.subscription?.plan?.name || '-' }}</span>
                            </template>
                        </el-table-column>

                        <el-table-column
                            sortable
                            prop="addon.name"
                            label="RCPA Name">
                            <template v-slot="scope">
                                <span>{{ scope.row.addon?.name || '-' }}</span>
                            </template>
                        </el-table-column>

                        <el-table-column
                            sortable
                            label="Status"
                            width="150">
                            <template v-slot="scope">
                                <el-tag
                                    :type="scope.row.processed ? 'success' : 'warning'"
                                    class="status-tag">
                                    {{ scope.row.processed ? 'Credited' : 'Pending' }}
                                </el-tag>
                            </template>
                        </el-table-column>

                        <el-table-column
                            sortable
                            prop="credited_at"
                            label="Credited At"
                            width="180">
                            <template v-slot="scope">
                                <span>{{ scope.row.credited_at || '-' }}</span>
                            </template>
                        </el-table-column>

                        <el-table-column
                            label="Actions"
                            width="180">
                            <template v-slot="scope">
                                <div class="action-buttons">
                                    <el-tooltip
                                        content="Credits have already been processed for this period"
                                        placement="top"
                                        :disabled="!scope.row.processed">
                                        <el-button
                                            size="mini"
                                            type="primary"
                                            :class="{ 'btn-disabled': scope.row.processed }"
                                            :loading="processing && selectedRecordId === scope.row.id"
                                            :disabled="scope.row.processed"
                                            @click="giveCredits(scope.row)">
                                            {{ scope.row.processed ? 'Processed' : 'Verify Payment' }}
                                        </el-button>
                                    </el-tooltip>

                                    <el-button
                                        size="mini"
                                        type="success"
                                        @click="openCompanyCard(scope.row)">
                                        Open Company Card
                                    </el-button>

                                    <el-button
                                        size="mini"
                                        class="chargebee-button"
                                        @click="goToChargebee(scope.row)">
                                        Go to Chargebee
                                    </el-button>
                                </div>
                            </template>
                        </el-table-column>
                    </el-table>
                </div>

                <!-- Pagination -->
                <div class="mt-3 mb-3" v-if="records.length > 0 && pagination.total > pagination.per_page">
                    <el-pagination
                        :current-page="pagination.current_page"
                        :page-size="pagination.per_page"
                        :page-sizes="[10, 15, 20, 25]"
                        :total="pagination.total"
                        background
                        class="pull-right"
                        layout="sizes, prev, pager, next"
                        @current-change="paginationChangePage"
                        @size-change="paginationChangeSize">
                    </el-pagination>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'

export default {
    data() {
        return {
            loading: false,
            records: [],
            selectedRecordId: null,
            processing: false,
            no_results: false,
            pagination: {
                per_page: 10,
                current_page: 1,
                total: 0
            },
            filters: {
                search_text: '',
                processed_order: {}
            }
        }
    },

    computed: {
        showClearFiltersButton() {
            return this.filters.search_text !== ''
        }
    },

    mounted() {
        this.handleSearch()
    },

    methods: {
        getCompany(row) {
            return row.subscription?.company || {}
        },

        clearFilters() {
            this.filters.search_text = ''
            this.records = []
            this.fetchData()
        },

        paginationChangePage(val) {
            this.pagination.current_page = val
            this.fetchData()
        },

        paginationChangeSize(val) {
            this.pagination.current_page = 1
            this.pagination.per_page = val
            this.fetchData()
        },

        handleSearch() {
            this.pagination.current_page = 1
            this.fetchData()
        },

        fetchData() {
            this.loading = true
            const params = {
                page: this.pagination.current_page,
                per_page: this.pagination.per_page,
                search_text: this.filters.search_text,
                ...this.filters.processed_order
            }

            axios.get('/api/admin/recurring-credit-addons', { params })
                .then(response => {
                    if (response.data) {
                        this.records = response.data.data
                        this.pagination.total = response.data.total || 0
                        this.no_results = this.records.length === 0
                    } else {
                        this.records = []
                        this.pagination.total = 0
                        this.no_results = true
                    }
                })
                .catch(error => {
                    this.records = []
                    this.pagination.total = 0
                    this.no_results = true
                    this.$notify.error({
                        title: 'Error',
                        message: error.response?.data?.message || 'Failed to fetch recurring credit addons'
                    })
                })
                .finally(() => {
                    this.loading = false
                })
        },

        handleSortChange({ order }) {
            const sortOrder = order === 'ascending' ? 'asc' : 'desc'
            this.filters.processed_order = order ? { sort_by: 'processed', sort_order: sortOrder } : {}
            this.fetchData()
        },

        giveCredits(record) {
            this.processing = true
            this.selectedRecordId = record.id
            const company = this.getCompany(record)
            const addonData = record.addon?.data

            this.$confirm(
                `Once this action is performed, Company <b>${company.name}</b> payment will be confirmed. The account will receive ${addonData.price} credits${addonData.rcpa_bonus_dollar_credits > 0 ? ` and ${addonData.rcpa_bonus_dollar_credits} bonus credits` : ''}.<br><b>Note:</b> This action is irreversible.`,
                'Confirmation Step',
                {
                    confirmButtonText: 'Confirm',
                    cancelButtonText: 'Cancel',
                    type: 'warning',
                    customClass: 'recurring-credits-modal',
                    dangerouslyUseHTMLString: true
                }
            ).then(() => {
                axios.post(`/api/admin/company/${company.id}/payment-verified/${record.id}`)
                    .then(response => {
                        const index = this.records.findIndex(r => r.id === record.id)
                        if (index !== -1) {
                            this.$set(this.records, index, response.data.subscription_addon)
                        }

                        this.$notify({
                            title: 'Success',
                            message: 'Successfully added credits for the recurring agreement',
                            type: 'success'
                        })
                    })
                    .catch(error => {
                        this.$notify.error({
                            title: 'Error',
                            message: error.response?.data?.message || 'Failed to give credits'
                        })
                    })
                    .finally(() => {
                        this.processing = false
                        this.selectedRecordId = null
                    })
            }).catch(() => {
                this.processing = false
                this.selectedRecordId = null
            })
        },

        openCompanyCard(record) {
            const company = this.getCompany(record)
            window.open(`/admin/company/${company.id}`, '_blank')
        },

        goToChargebee(record) {
            if (record.subscription?.chargebee_id) {
                let env = 'aloware'
                if (['local', 'dev', 'mde', 'testing', 'staging'].includes(localStorage.getItem('app_env'))) {
                    env = 'aloware-test'
                }
                window.open(`https://${env}.chargebee.com/admin-console/subscriptions/${record.subscription.chargebee_id}`, '_blank')
            } else {
                this.$notify.error({
                    title: 'Error',
                    message: 'No Chargebee subscription found'
                })
            }
        },

        exportToCsv() {
            const params = {
                ...this.filters,
                export: true
            }

            axios.get('/api/admin/recurring-credit-addons', { params })
                .then(() => {
                    this.$notify.info({
                        offset: 95,
                        title: 'Exports',
                        dangerouslyUseHTMLString: true,
                        message: `Your export is being processed. You will receive an email with a download link when it's ready.`,
                        duration: 3000
                    });
                })
                .catch(error => {
                    this.$notify.error({
                        offset: 95,
                        title: 'Ops...',
                        dangerouslyUseHTMLString: true,
                        message: 'We something wrong, try to export this report again later.',
                        duration: 3000,
                    });
                })
        },
    },
    watch: {
        'filters.search_text': function () {
            if (this.filters.search_text.trim() == '') {
                this.records = []
                this.handleSearch()
            }
        }
    }
}
</script>

<style scoped>
.message-centered {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
}

.action-buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.action-buttons .el-button {
    margin: 0;
    width: 100%;
}

.action-buttons .el-button.btn-disabled {
    opacity: 0.7;
    cursor: not-allowed;
}

.action-buttons .el-button--mini {
    padding: 7px 12px;
    font-size: 12px;
}

.chargebee-button {
    background-color: #9c27b0 !important;
    border-color: #9c27b0 !important;
    color: white !important;
}

.chargebee-button:hover {
    background-color: #7b1fa2 !important;
    border-color: #7b1fa2 !important;
}

.el-table ::v-deep .cell {
    padding: 0 10px;
}

.status-tag {
    margin: 0;
    padding: 0 12px;
    height: 28px;
    line-height: 26px;
    font-size: 13px;
}

:deep(.el-table .el-table__row td:nth-child(6)) {
    padding: 8px !important;
}

.el-alert h4 {
    color: #000000;
    font-weight: 700;
    font-size: 14px;
}

.alert-text {
  color: #000000 !important;
  font-weight: 300;
}
</style>
